export type PasswordStrings = {
  newPassword: string;
  confirmPassword: string;
  showPassword: string;
  hidePassword: string;
  continueToLogin: string;
  changeTitle: string;
  changeBody: string;
  currentPassword: string;
  changePassword: string;
  passwordUnavailableTitle: string;
  passwordUnavailableBody: string;
  changeCompleteTitle: string;
  changeCompleteBody: string;
  checkingPassword: string;
  errors: Record<string, string>;
};

const catalog = {
  en: {
    newPassword: 'New password (at least 15 characters)',
    confirmPassword: 'Confirm new password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    continueToLogin: 'Continue to login',
    changeTitle: 'Password',
    changeBody:
      'Enter your current password before choosing a new one. Saving signs out every device.',
    currentPassword: 'Current password',
    changePassword: 'Change password',
    passwordUnavailableTitle: 'You sign in with Google',
    passwordUnavailableBody:
      'This account has no Poneva password. Manage the password with Google.',
    changeCompleteTitle: 'Password changed',
    changeCompleteBody: 'All sessions are signed out. Continue and log in with the new password.',
    checkingPassword: 'Checking sign-in method…',
    error_invalid_input: 'Enter a valid email address.',
    error_password_mismatch: 'The passwords do not match.',
    error_password_too_short: 'The password must be at least 15 characters.',
    error_password_compromised:
      'That password is known from previous data breaches. Choose a unique passphrase.',
    error_password_too_long: 'The password is too long.',
    error_password_reused: 'Choose a password that is different from the current one.',
    error_current_password_wrong: 'The current password is incorrect.',
    error_password_unavailable: 'This account does not use a password.',
    error_password_changed: 'The password changed in another session. Try again.',
    error_reset_expired: 'The link is invalid, expired, or has already been used.',
    error_rate_limited: 'Too many attempts. Wait a moment and try again.',
    error_server_unavailable: 'The service is unavailable right now. Please try again shortly.',
    error_generic: 'Something went wrong. Please try again.',
  },
  sv: {
    newPassword: 'Nytt lösenord (minst 15 tecken)',
    confirmPassword: 'Bekräfta nytt lösenord',
    showPassword: 'Visa lösenord',
    hidePassword: 'Dölj lösenord',
    continueToLogin: 'Fortsätt till inloggning',
    changeTitle: 'Lösenord',
    changeBody:
      'Ange ditt nuvarande lösenord innan du väljer ett nytt. Alla enheter loggas ut när du sparar.',
    currentPassword: 'Nuvarande lösenord',
    changePassword: 'Ändra lösenord',
    passwordUnavailableTitle: 'Du loggar in med Google',
    passwordUnavailableBody:
      'Det här kontot har inget Poneva-lösenord. Hantera lösenordet hos Google.',
    changeCompleteTitle: 'Lösenordet är ändrat',
    changeCompleteBody:
      'Alla sessioner är utloggade. Fortsätt och logga in med det nya lösenordet.',
    checkingPassword: 'Kontrollerar inloggningsmetod…',
    error_invalid_input: 'Ange en giltig e-postadress.',
    error_password_mismatch: 'Lösenorden matchar inte.',
    error_password_too_short: 'Lösenordet måste vara minst 15 tecken.',
    error_password_compromised:
      'Det lösenordet är känt från tidigare dataläckor. Välj en unik lösenfras.',
    error_password_too_long: 'Lösenordet är för långt.',
    error_password_reused: 'Välj ett annat lösenord än det nuvarande.',
    error_current_password_wrong: 'Det nuvarande lösenordet är fel.',
    error_password_unavailable: 'Det här kontot använder inte lösenord.',
    error_password_changed: 'Lösenordet ändrades i en annan session. Försök igen.',
    error_reset_expired: 'Länken är ogiltig, har gått ut eller har redan använts.',
    error_rate_limited: 'För många försök. Vänta en stund och försök igen.',
    error_server_unavailable: 'Tjänsten är inte tillgänglig just nu. Försök igen om en stund.',
    error_generic: 'Något gick fel. Försök igen.',
  },
  bg: {
    newPassword: 'Нова парола (поне 15 знака)',
    confirmPassword: 'Потвърдете новата парола',
    showPassword: 'Показване на паролата',
    hidePassword: 'Скриване на паролата',
    continueToLogin: 'Продължаване към вход',
    changeTitle: 'Парола',
    changeBody:
      'Въведете текущата си парола, преди да изберете нова. При запазване ще излезете от всички устройства.',
    currentPassword: 'Текуща парола',
    changePassword: 'Промяна на паролата',
    passwordUnavailableTitle: 'Влизате с Google',
    passwordUnavailableBody:
      'Този профил няма парола за Poneva. Управлявайте паролата си чрез Google.',
    changeCompleteTitle: 'Паролата е променена',
    changeCompleteBody: 'От всички сесии е извършен изход. Продължете и влезте с новата парола.',
    checkingPassword: 'Проверка на метода за вход…',
    error_invalid_input: 'Въведете валиден имейл адрес.',
    error_password_mismatch: 'Паролите не съвпадат.',
    error_password_too_short: 'Паролата трябва да съдържа поне 15 знака.',
    error_password_compromised:
      'Тази парола е известна от предишни изтичания на данни. Изберете уникална парола.',
    error_password_too_long: 'Паролата е твърде дълга.',
    error_password_reused: 'Изберете парола, различна от текущата.',
    error_current_password_wrong: 'Текущата парола е неправилна.',
    error_password_unavailable: 'Този профил не използва парола.',
    error_password_changed: 'Паролата е променена в друга сесия. Опитайте отново.',
    error_reset_expired: 'Връзката е невалидна, изтекла е или вече е използвана.',
    error_rate_limited: 'Твърде много опити. Изчакайте малко и опитайте отново.',
    error_server_unavailable: 'Услугата временно не е достъпна. Опитайте отново след малко.',
    error_generic: 'Нещо се обърка. Опитайте отново.',
  },
  hr: {
    newPassword: 'Nova lozinka (najmanje 15 znakova)',
    confirmPassword: 'Potvrdite novu lozinku',
    showPassword: 'Prikaži lozinku',
    hidePassword: 'Sakrij lozinku',
    continueToLogin: 'Nastavite na prijavu',
    changeTitle: 'Lozinka',
    changeBody:
      'Unesite trenutačnu lozinku prije nego što odaberete novu. Spremanjem ćete odjaviti sve uređaje.',
    currentPassword: 'Trenutačna lozinka',
    changePassword: 'Promijeni lozinku',
    passwordUnavailableTitle: 'Prijavljujete se Google računom',
    passwordUnavailableBody:
      'Ovaj račun nema lozinku za Ponevu. Upravljajte lozinkom putem Googlea.',
    changeCompleteTitle: 'Lozinka je promijenjena',
    changeCompleteBody: 'Sve su sesije odjavljene. Nastavite i prijavite se novom lozinkom.',
    checkingPassword: 'Provjera načina prijave…',
    error_invalid_input: 'Unesite valjanu adresu e-pošte.',
    error_password_mismatch: 'Lozinke se ne podudaraju.',
    error_password_too_short: 'Lozinka mora sadržavati najmanje 15 znakova.',
    error_password_compromised:
      'Ta je lozinka poznata iz prethodnih povreda podataka. Odaberite jedinstvenu zaporku.',
    error_password_too_long: 'Lozinka je predugačka.',
    error_password_reused: 'Odaberite lozinku koja se razlikuje od trenutačne.',
    error_current_password_wrong: 'Trenutačna lozinka nije točna.',
    error_password_unavailable: 'Ovaj račun ne upotrebljava lozinku.',
    error_password_changed: 'Lozinka je promijenjena u drugoj sesiji. Pokušajte ponovno.',
    error_reset_expired: 'Poveznica nije valjana, istekla je ili je već iskorištena.',
    error_rate_limited: 'Previše pokušaja. Pričekajte trenutak i pokušajte ponovno.',
    error_server_unavailable: 'Usluga trenutačno nije dostupna. Pokušajte ponovno uskoro.',
    error_generic: 'Nešto je pošlo po zlu. Pokušajte ponovno.',
  },
  cs: {
    newPassword: 'Nové heslo (alespoň 15 znaků)',
    confirmPassword: 'Potvrdit nové heslo',
    showPassword: 'Zobrazit heslo',
    hidePassword: 'Skrýt heslo',
    continueToLogin: 'Pokračovat k přihlášení',
    changeTitle: 'Heslo',
    changeBody:
      'Před výběrem nového hesla zadejte své aktuální heslo. Uložením se odhlásíte ze všech zařízení.',
    currentPassword: 'Aktuální heslo',
    changePassword: 'Změnit heslo',
    passwordUnavailableTitle: 'Přihlašujete se pomocí Googlu',
    passwordUnavailableBody: 'Tento účet nemá heslo Poneva. Heslo spravujte u Googlu.',
    changeCompleteTitle: 'Heslo změněno',
    changeCompleteBody: 'Všechny relace byly odhlášeny. Pokračujte a přihlaste se novým heslem.',
    checkingPassword: 'Kontrola způsobu přihlášení…',
    error_invalid_input: 'Zadejte platnou e-mailovou adresu.',
    error_password_mismatch: 'Hesla se neshodují.',
    error_password_too_short: 'Heslo musí mít alespoň 15 znaků.',
    error_password_compromised:
      'Toto heslo je známé z předchozích úniků dat. Zvolte jedinečnou přístupovou frázi.',
    error_password_too_long: 'Heslo je příliš dlouhé.',
    error_password_reused: 'Zvolte heslo, které se liší od aktuálního hesla.',
    error_current_password_wrong: 'Aktuální heslo není správné.',
    error_password_unavailable: 'Tento účet heslo nepoužívá.',
    error_password_changed: 'Heslo bylo změněno v jiné relaci. Zkuste to znovu.',
    error_reset_expired: 'Odkaz je neplatný, jeho platnost vypršela nebo již byl použit.',
    error_rate_limited: 'Příliš mnoho pokusů. Chvíli počkejte a zkuste to znovu.',
    error_server_unavailable: 'Služba je momentálně nedostupná. Zkuste to prosím za chvíli znovu.',
    error_generic: 'Něco se pokazilo. Zkuste to prosím znovu.',
  },
  da: {
    newPassword: 'Ny adgangskode (mindst 15 tegn)',
    confirmPassword: 'Bekræft ny adgangskode',
    showPassword: 'Vis adgangskode',
    hidePassword: 'Skjul adgangskode',
    continueToLogin: 'Fortsæt til login',
    changeTitle: 'Adgangskode',
    changeBody:
      'Indtast din nuværende adgangskode, før du vælger en ny. Når du gemmer, logges du ud på alle enheder.',
    currentPassword: 'Nuværende adgangskode',
    changePassword: 'Skift adgangskode',
    passwordUnavailableTitle: 'Du logger ind med Google',
    passwordUnavailableBody:
      'Denne konto har ingen Poneva-adgangskode. Administrer adgangskoden hos Google.',
    changeCompleteTitle: 'Adgangskoden er ændret',
    changeCompleteBody: 'Alle sessioner er logget ud. Fortsæt, og log ind med den nye adgangskode.',
    checkingPassword: 'Tjekker loginmetode…',
    error_invalid_input: 'Indtast en gyldig e-mailadresse.',
    error_password_mismatch: 'Adgangskoderne stemmer ikke overens.',
    error_password_too_short: 'Adgangskoden skal være mindst 15 tegn.',
    error_password_compromised:
      'Denne adgangskode er kendt fra tidligere datalæk. Vælg en unik adgangssætning.',
    error_password_too_long: 'Adgangskoden er for lang.',
    error_password_reused: 'Vælg en adgangskode, der er anderledes end den nuværende.',
    error_current_password_wrong: 'Den nuværende adgangskode er forkert.',
    error_password_unavailable: 'Denne konto bruger ikke en adgangskode.',
    error_password_changed: 'Adgangskoden blev ændret i en anden session. Prøv igen.',
    error_reset_expired: 'Linket er ugyldigt, udløbet eller allerede blevet brugt.',
    error_rate_limited: 'For mange forsøg. Vent et øjeblik, og prøv igen.',
    error_server_unavailable: 'Tjenesten er ikke tilgængelig lige nu. Prøv igen om lidt.',
    error_generic: 'Noget gik galt. Prøv igen.',
  },
  nl: {
    newPassword: 'Nieuw wachtwoord (minimaal 15 tekens)',
    confirmPassword: 'Nieuw wachtwoord bevestigen',
    showPassword: 'Wachtwoord tonen',
    hidePassword: 'Wachtwoord verbergen',
    continueToLogin: 'Doorgaan naar inloggen',
    changeTitle: 'Wachtwoord',
    changeBody:
      'Voer je huidige wachtwoord in voordat je een nieuw wachtwoord kiest. Na het opslaan word je op elk apparaat uitgelogd.',
    currentPassword: 'Huidig wachtwoord',
    changePassword: 'Wachtwoord wijzigen',
    passwordUnavailableTitle: 'Je logt in met Google',
    passwordUnavailableBody:
      'Dit account heeft geen Poneva-wachtwoord. Beheer het wachtwoord via Google.',
    changeCompleteTitle: 'Wachtwoord gewijzigd',
    changeCompleteBody: 'Alle sessies zijn beëindigd. Ga door en log in met het nieuwe wachtwoord.',
    checkingPassword: 'Aanmeldmethode controleren…',
    error_invalid_input: 'Voer een geldig e-mailadres in.',
    error_password_mismatch: 'De wachtwoorden komen niet overeen.',
    error_password_too_short: 'Het wachtwoord moet minimaal 15 tekens bevatten.',
    error_password_compromised:
      'Dit wachtwoord komt voor in eerdere datalekken. Kies een unieke wachtzin.',
    error_password_too_long: 'Het wachtwoord is te lang.',
    error_password_reused: 'Kies een wachtwoord dat verschilt van het huidige wachtwoord.',
    error_current_password_wrong: 'Het huidige wachtwoord is onjuist.',
    error_password_unavailable: 'Dit account gebruikt geen wachtwoord.',
    error_password_changed:
      'Het wachtwoord is in een andere sessie gewijzigd. Probeer het opnieuw.',
    error_reset_expired: 'De link is ongeldig, verlopen of al gebruikt.',
    error_rate_limited: 'Te veel pogingen. Wacht even en probeer het opnieuw.',
    error_server_unavailable:
      'De service is momenteel niet beschikbaar. Probeer het binnenkort opnieuw.',
    error_generic: 'Er is iets misgegaan. Probeer het opnieuw.',
  },
  et: {
    newPassword: 'Uus parool (vähemalt 15 märki)',
    confirmPassword: 'Kinnita uus parool',
    showPassword: 'Näita parooli',
    hidePassword: 'Peida parool',
    continueToLogin: 'Jätka sisselogimisega',
    changeTitle: 'Parool',
    changeBody:
      'Enne uue parooli valimist sisesta oma praegune parool. Salvestamisel logitakse sind kõigist seadmetest välja.',
    currentPassword: 'Praegune parool',
    changePassword: 'Muuda parooli',
    passwordUnavailableTitle: "Logid sisse Google'iga",
    passwordUnavailableBody: "Sellel kontol pole Poneva parooli. Halda parooli Google'is.",
    changeCompleteTitle: 'Parool muudetud',
    changeCompleteBody: 'Kõigist seanssidest logiti välja. Jätka ja logi sisse uue parooliga.',
    checkingPassword: 'Sisselogimisviisi kontrollimine…',
    error_invalid_input: 'Sisesta kehtiv e-posti aadress.',
    error_password_mismatch: 'Paroolid ei ühti.',
    error_password_too_short: 'Parool peab sisaldama vähemalt 15 märki.',
    error_password_compromised:
      'See parool on teada varasematest andmeleketest. Vali ainulaadne paroolifraas.',
    error_password_too_long: 'Parool on liiga pikk.',
    error_password_reused: 'Vali praegusest erinev parool.',
    error_current_password_wrong: 'Praegune parool on vale.',
    error_password_unavailable: 'See konto ei kasuta parooli.',
    error_password_changed: 'Parooli muudeti teises seansis. Proovi uuesti.',
    error_reset_expired: 'Link on kehtetu, aegunud või juba kasutatud.',
    error_rate_limited: 'Liiga palju katseid. Oota hetk ja proovi uuesti.',
    error_server_unavailable: 'Teenus pole praegu saadaval. Proovi veidi hiljem uuesti.',
    error_generic: 'Midagi läks valesti. Proovi uuesti.',
  },
  fi: {
    newPassword: 'Uusi salasana (vähintään 15 merkkiä)',
    confirmPassword: 'Vahvista uusi salasana',
    showPassword: 'Näytä salasana',
    hidePassword: 'Piilota salasana',
    continueToLogin: 'Jatka kirjautumiseen',
    changeTitle: 'Salasana',
    changeBody:
      'Anna nykyinen salasanasi ennen uuden valitsemista. Tallentaminen kirjaa sinut ulos kaikilta laitteilta.',
    currentPassword: 'Nykyinen salasana',
    changePassword: 'Vaihda salasana',
    passwordUnavailableTitle: 'Kirjaudut sisään Googlella',
    passwordUnavailableBody:
      'Tällä tilillä ei ole Poneva-salasanaa. Hallinnoi salasanaa Googlessa.',
    changeCompleteTitle: 'Salasana vaihdettu',
    changeCompleteBody:
      'Kaikki istunnot on kirjattu ulos. Jatka ja kirjaudu sisään uudella salasanalla.',
    checkingPassword: 'Tarkistetaan kirjautumistapaa…',
    error_invalid_input: 'Anna kelvollinen sähköpostiosoite.',
    error_password_mismatch: 'Salasanat eivät täsmää.',
    error_password_too_short: 'Salasanan on oltava vähintään 15 merkkiä pitkä.',
    error_password_compromised:
      'Salasana on tunnettu aiemmista tietomurroista. Valitse ainutkertainen tunnuslause.',
    error_password_too_long: 'Salasana on liian pitkä.',
    error_password_reused: 'Valitse nykyisestä salasanasta poikkeava salasana.',
    error_current_password_wrong: 'Nykyinen salasana on väärä.',
    error_password_unavailable: 'Tällä tilillä ei käytetä salasanaa.',
    error_password_changed: 'Salasana vaihdettiin toisessa istunnossa. Yritä uudelleen.',
    error_reset_expired: 'Linkki on virheellinen, vanhentunut tai sitä on jo käytetty.',
    error_rate_limited: 'Liian monta yritystä. Odota hetki ja yritä uudelleen.',
    error_server_unavailable:
      'Palvelu ei ole juuri nyt käytettävissä. Yritä hetken kuluttua uudelleen.',
    error_generic: 'Jokin meni pieleen. Yritä uudelleen.',
  },
  fr: {
    newPassword: 'Nouveau mot de passe (au moins 15 caractères)',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    showPassword: 'Afficher le mot de passe',
    hidePassword: 'Masquer le mot de passe',
    continueToLogin: 'Continuer vers la connexion',
    changeTitle: 'Mot de passe',
    changeBody:
      'Saisissez votre mot de passe actuel avant d’en choisir un nouveau. L’enregistrement déconnectera tous les appareils.',
    currentPassword: 'Mot de passe actuel',
    changePassword: 'Modifier le mot de passe',
    passwordUnavailableTitle: 'Vous vous connectez avec Google',
    passwordUnavailableBody:
      'Ce compte n’a pas de mot de passe Poneva. Gérez votre mot de passe avec Google.',
    changeCompleteTitle: 'Mot de passe modifié',
    changeCompleteBody:
      'Toutes les sessions sont déconnectées. Continuez et connectez-vous avec le nouveau mot de passe.',
    checkingPassword: 'Vérification du mode de connexion…',
    error_invalid_input: 'Saisissez une adresse e-mail valide.',
    error_password_mismatch: 'Les mots de passe ne correspondent pas.',
    error_password_too_short: 'Le mot de passe doit comporter au moins 15 caractères.',
    error_password_compromised:
      'Ce mot de passe figure dans des fuites de données antérieures. Choisissez une phrase secrète unique.',
    error_password_too_long: 'Le mot de passe est trop long.',
    error_password_reused: 'Choisissez un mot de passe différent de l’actuel.',
    error_current_password_wrong: 'Le mot de passe actuel est incorrect.',
    error_password_unavailable: 'Ce compte n’utilise pas de mot de passe.',
    error_password_changed: 'Le mot de passe a été modifié dans une autre session. Réessayez.',
    error_reset_expired: 'Le lien est invalide, a expiré ou a déjà été utilisé.',
    error_rate_limited: 'Trop de tentatives. Attendez un instant, puis réessayez.',
    error_server_unavailable:
      'Le service est momentanément indisponible. Réessayez dans quelques instants.',
    error_generic: 'Une erreur s’est produite. Réessayez.',
  },
  de: {
    newPassword: 'Neues Passwort (mindestens 15 Zeichen)',
    confirmPassword: 'Neues Passwort bestätigen',
    showPassword: 'Passwort anzeigen',
    hidePassword: 'Passwort ausblenden',
    continueToLogin: 'Weiter zur Anmeldung',
    changeTitle: 'Passwort',
    changeBody:
      'Gib dein aktuelles Passwort ein, bevor du ein neues auswählst. Beim Speichern wirst du auf allen Geräten abgemeldet.',
    currentPassword: 'Aktuelles Passwort',
    changePassword: 'Passwort ändern',
    passwordUnavailableTitle: 'Du meldest dich mit Google an',
    passwordUnavailableBody:
      'Für dieses Konto gibt es kein Poneva-Passwort. Verwalte das Passwort über Google.',
    changeCompleteTitle: 'Passwort geändert',
    changeCompleteBody:
      'Alle Sitzungen wurden beendet. Fahre fort und melde dich mit dem neuen Passwort an.',
    checkingPassword: 'Anmeldemethode wird überprüft…',
    error_invalid_input: 'Gib eine gültige E-Mail-Adresse ein.',
    error_password_mismatch: 'Die Passwörter stimmen nicht überein.',
    error_password_too_short: 'Das Passwort muss mindestens 15 Zeichen lang sein.',
    error_password_compromised:
      'Dieses Passwort ist aus früheren Datenschutzverletzungen bekannt. Wähle eine einzigartige Passphrase.',
    error_password_too_long: 'Das Passwort ist zu lang.',
    error_password_reused: 'Wähle ein anderes Passwort als dein aktuelles.',
    error_current_password_wrong: 'Das aktuelle Passwort ist falsch.',
    error_password_unavailable: 'Für dieses Konto wird kein Passwort verwendet.',
    error_password_changed:
      'Das Passwort wurde in einer anderen Sitzung geändert. Versuche es erneut.',
    error_reset_expired: 'Der Link ist ungültig, abgelaufen oder wurde bereits verwendet.',
    error_rate_limited: 'Zu viele Versuche. Warte einen Moment und versuche es erneut.',
    error_server_unavailable:
      'Der Dienst ist momentan nicht verfügbar. Versuche es bitte in Kürze erneut.',
    error_generic: 'Etwas ist schiefgelaufen. Versuche es bitte erneut.',
  },
  el: {
    newPassword: 'Νέος κωδικός πρόσβασης (τουλάχιστον 15 χαρακτήρες)',
    confirmPassword: 'Επιβεβαίωση νέου κωδικού πρόσβασης',
    showPassword: 'Εμφάνιση κωδικού πρόσβασης',
    hidePassword: 'Απόκρυψη κωδικού πρόσβασης',
    continueToLogin: 'Συνέχεια στη σύνδεση',
    changeTitle: 'Κωδικός πρόσβασης',
    changeBody:
      'Εισαγάγετε τον τρέχοντα κωδικό πρόσβασής σας πριν επιλέξετε νέο. Με την αποθήκευση αποσυνδέεστε από κάθε συσκευή.',
    currentPassword: 'Τρέχων κωδικός πρόσβασης',
    changePassword: 'Αλλαγή κωδικού πρόσβασης',
    passwordUnavailableTitle: 'Συνδέεστε με Google',
    passwordUnavailableBody:
      'Αυτός ο λογαριασμός δεν διαθέτει κωδικό πρόσβασης Poneva. Διαχειριστείτε τον κωδικό πρόσβασης μέσω Google.',
    changeCompleteTitle: 'Ο κωδικός πρόσβασης άλλαξε',
    changeCompleteBody:
      'Όλες οι συνεδρίες αποσυνδέθηκαν. Συνεχίστε και συνδεθείτε με τον νέο κωδικό πρόσβασης.',
    checkingPassword: 'Έλεγχος μεθόδου σύνδεσης…',
    error_invalid_input: 'Εισαγάγετε μια έγκυρη διεύθυνση email.',
    error_password_mismatch: 'Οι κωδικοί πρόσβασης δεν ταιριάζουν.',
    error_password_too_short: 'Ο κωδικός πρόσβασης πρέπει να περιέχει τουλάχιστον 15 χαρακτήρες.',
    error_password_compromised:
      'Αυτός ο κωδικός πρόσβασης είναι γνωστός από προηγούμενες παραβιάσεις δεδομένων. Επιλέξτε μια μοναδική φράση πρόσβασης.',
    error_password_too_long: 'Ο κωδικός πρόσβασης είναι πολύ μεγάλος.',
    error_password_reused: 'Επιλέξτε κωδικό πρόσβασης διαφορετικό από τον τρέχοντα.',
    error_current_password_wrong: 'Ο τρέχων κωδικός πρόσβασης είναι εσφαλμένος.',
    error_password_unavailable: 'Αυτός ο λογαριασμός δεν χρησιμοποιεί κωδικό πρόσβασης.',
    error_password_changed: 'Ο κωδικός πρόσβασης άλλαξε σε άλλη συνεδρία. Δοκιμάστε ξανά.',
    error_reset_expired: 'Ο σύνδεσμος δεν είναι έγκυρος, έχει λήξει ή έχει ήδη χρησιμοποιηθεί.',
    error_rate_limited: 'Έγιναν πάρα πολλές προσπάθειες. Περιμένετε λίγο και δοκιμάστε ξανά.',
    error_server_unavailable:
      'Η υπηρεσία δεν είναι διαθέσιμη αυτήν τη στιγμή. Δοκιμάστε ξανά σύντομα.',
    error_generic: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.',
  },
  hu: {
    newPassword: 'Új jelszó (legalább 15 karakter)',
    confirmPassword: 'Új jelszó megerősítése',
    showPassword: 'Jelszó megjelenítése',
    hidePassword: 'Jelszó elrejtése',
    continueToLogin: 'Tovább a bejelentkezéshez',
    changeTitle: 'Jelszó',
    changeBody:
      'Az új jelszó kiválasztása előtt add meg a jelenlegi jelszavadat. A mentés minden eszközről kijelentkeztet.',
    currentPassword: 'Jelenlegi jelszó',
    changePassword: 'Jelszó módosítása',
    passwordUnavailableTitle: 'Google-fiókkal jelentkezel be',
    passwordUnavailableBody:
      'Ehhez a fiókhoz nem tartozik Poneva-jelszó. A jelszót a Google-nél kezelheted.',
    changeCompleteTitle: 'Jelszó módosítva',
    changeCompleteBody:
      'Minden munkamenetből kijelentkeztünk. Folytasd, és jelentkezz be az új jelszóval.',
    checkingPassword: 'Bejelentkezési mód ellenőrzése…',
    error_invalid_input: 'Adj meg érvényes e-mail-címet.',
    error_password_mismatch: 'A jelszavak nem egyeznek.',
    error_password_too_short: 'A jelszónak legalább 15 karakterből kell állnia.',
    error_password_compromised:
      'Ez a jelszó korábbi adatvédelmi incidensekben már nyilvánosságra került. Válassz egy egyedi jelmondatot.',
    error_password_too_long: 'A jelszó túl hosszú.',
    error_password_reused: 'Válassz a jelenlegitől eltérő jelszót.',
    error_current_password_wrong: 'A jelenlegi jelszó helytelen.',
    error_password_unavailable: 'Ez a fiók nem használ jelszót.',
    error_password_changed: 'A jelszó egy másik munkamenetben módosult. Próbáld újra.',
    error_reset_expired: 'A link érvénytelen, lejárt vagy már felhasználták.',
    error_rate_limited: 'Túl sok próbálkozás. Várj egy kicsit, majd próbáld újra.',
    error_server_unavailable: 'A szolgáltatás jelenleg nem érhető el. Próbáld újra rövidesen.',
    error_generic: 'Hiba történt. Próbáld újra.',
  },
  ga: {
    newPassword: 'Focal faire nua (15 charachtar ar a laghad)',
    confirmPassword: 'Deimhnigh an focal faire nua',
    showPassword: 'Taispeáin an focal faire',
    hidePassword: 'Folaigh an focal faire',
    continueToLogin: 'Lean ar aghaidh chuig logáil isteach',
    changeTitle: 'Focal faire',
    changeBody:
      'Cuir isteach d’fhocal faire reatha sula roghnaíonn tú ceann nua. Sínítear amach as gach gléas nuair a shábhálann tú é.',
    currentPassword: 'Focal faire reatha',
    changePassword: 'Athraigh an focal faire',
    passwordUnavailableTitle: 'Síníonn tú isteach le Google',
    passwordUnavailableBody:
      'Níl focal faire Poneva ag an gcuntas seo. Bainistigh an focal faire le Google.',
    changeCompleteTitle: 'Athraíodh an focal faire',
    changeCompleteBody:
      'Síníodh amach as gach seisiún. Lean ar aghaidh agus logáil isteach leis an bhfocal faire nua.',
    checkingPassword: 'Modh sínithe isteach á sheiceáil…',
    error_invalid_input: 'Cuir isteach seoladh ríomhphoist bailí.',
    error_password_mismatch: 'Ní hionann na focail faire.',
    error_password_too_short: 'Caithfidh 15 charachtar ar a laghad a bheith sa bhfocal faire.',
    error_password_compromised:
      'Tá an focal faire sin ar eolas ó sceitheadh sonraí roimhe seo. Roghnaigh frása faire uathúil.',
    error_password_too_long: 'Tá an focal faire rófhada.',
    error_password_reused: 'Roghnaigh focal faire atá difriúil ón gceann reatha.',
    error_current_password_wrong: 'Tá an focal faire reatha mícheart.',
    error_password_unavailable: 'Ní úsáideann an cuntas seo focal faire.',
    error_password_changed: 'Athraíodh an focal faire i seisiún eile. Bain triail eile as.',
    error_reset_expired: 'Tá an nasc neamhbhailí, imithe in éag nó úsáideadh cheana é.',
    error_rate_limited: 'An iomarca iarrachtaí. Fan nóiméad agus bain triail eile as.',
    error_server_unavailable:
      'Níl an tseirbhís ar fáil faoi láthair. Bain triail eile as gan mhoill.',
    error_generic: 'Tharla earráid. Bain triail eile as.',
  },
  it: {
    newPassword: 'Nuova password (almeno 15 caratteri)',
    confirmPassword: 'Conferma la nuova password',
    showPassword: 'Mostra password',
    hidePassword: 'Nascondi password',
    continueToLogin: 'Continua per accedere',
    changeTitle: 'Password',
    changeBody:
      'Inserisci la password attuale prima di sceglierne una nuova. Il salvataggio disconnette tutti i dispositivi.',
    currentPassword: 'Password attuale',
    changePassword: 'Cambia password',
    passwordUnavailableTitle: 'Accedi con Google',
    passwordUnavailableBody:
      'Questo account non ha una password Poneva. Gestisci la password con Google.',
    changeCompleteTitle: 'Password modificata',
    changeCompleteBody:
      'Tutte le sessioni sono state disconnesse. Continua e accedi con la nuova password.',
    checkingPassword: 'Verifica del metodo di accesso…',
    error_invalid_input: 'Inserisci un indirizzo email valido.',
    error_password_mismatch: 'Le password non corrispondono.',
    error_password_too_short: 'La password deve contenere almeno 15 caratteri.',
    error_password_compromised:
      'Questa password è nota per essere comparsa in precedenti violazioni di dati. Scegli una passphrase univoca.',
    error_password_too_long: 'La password è troppo lunga.',
    error_password_reused: 'Scegli una password diversa da quella attuale.',
    error_current_password_wrong: 'La password attuale non è corretta.',
    error_password_unavailable: 'Questo account non usa una password.',
    error_password_changed: "La password è stata modificata in un'altra sessione. Riprova.",
    error_reset_expired: 'Il link non è valido, è scaduto o è già stato utilizzato.',
    error_rate_limited: 'Troppi tentativi. Attendi qualche istante e riprova.',
    error_server_unavailable: 'Il servizio non è al momento disponibile. Riprova tra poco.',
    error_generic: 'Si è verificato un errore. Riprova.',
  },
  lv: {
    newPassword: 'Jaunā parole (vismaz 15 rakstzīmes)',
    confirmPassword: 'Apstipriniet jauno paroli',
    showPassword: 'Rādīt paroli',
    hidePassword: 'Paslēpt paroli',
    continueToLogin: 'Turpināt uz pieteikšanos',
    changeTitle: 'Parole',
    changeBody:
      'Ievadiet pašreizējo paroli, pirms izvēlaties jaunu. Pēc saglabāšanas tiksiet izrakstīts no visām ierīcēm.',
    currentPassword: 'Pašreizējā parole',
    changePassword: 'Mainīt paroli',
    passwordUnavailableTitle: 'Jūs piesakāties, izmantojot Google',
    passwordUnavailableBody: 'Šim kontam nav Poneva paroles. Pārvaldiet paroli Google kontā.',
    changeCompleteTitle: 'Parole mainīta',
    changeCompleteBody: 'Visas sesijas ir slēgtas. Turpiniet un piesakieties ar jauno paroli.',
    checkingPassword: 'Notiek pieteikšanās metodes pārbaude…',
    error_invalid_input: 'Ievadiet derīgu e-pasta adresi.',
    error_password_mismatch: 'Paroles nesakrīt.',
    error_password_too_short: 'Parolei jābūt vismaz 15 rakstzīmēm garai.',
    error_password_compromised:
      'Šī parole ir atrodama iepriekšējo datu noplūžu sarakstos. Izvēlieties unikālu paroles frāzi.',
    error_password_too_long: 'Parole ir pārāk gara.',
    error_password_reused: 'Izvēlieties paroli, kas atšķiras no pašreizējās.',
    error_current_password_wrong: 'Pašreizējā parole ir nepareiza.',
    error_password_unavailable: 'Šis konts neizmanto paroli.',
    error_password_changed: 'Parole ir mainīta citā sesijā. Mēģiniet vēlreiz.',
    error_reset_expired:
      'Saite nav derīga, tās derīguma termiņš ir beidzies vai tā jau ir izmantota.',
    error_rate_limited: 'Pārāk daudz mēģinājumu. Uzgaidiet brīdi un mēģiniet vēlreiz.',
    error_server_unavailable: 'Pakalpojums pašlaik nav pieejams. Lūdzu, drīzumā mēģiniet vēlreiz.',
    error_generic: 'Radās kļūda. Lūdzu, mēģiniet vēlreiz.',
  },
  lt: {
    newPassword: 'Naujas slaptažodis (mažiausiai 15 simbolių)',
    confirmPassword: 'Patvirtinkite naują slaptažodį',
    showPassword: 'Rodyti slaptažodį',
    hidePassword: 'Slėpti slaptažodį',
    continueToLogin: 'Tęsti prisijungimą',
    changeTitle: 'Slaptažodis',
    changeBody:
      'Prieš pasirinkdami naują slaptažodį įveskite dabartinį. Išsaugojus būsite atjungti visuose įrenginiuose.',
    currentPassword: 'Dabartinis slaptažodis',
    changePassword: 'Keisti slaptažodį',
    passwordUnavailableTitle: 'Prisijungiate naudodami „Google“',
    passwordUnavailableBody:
      'Ši paskyra neturi Poneva slaptažodžio. Slaptažodį tvarkykite naudodami „Google“.',
    changeCompleteTitle: 'Slaptažodis pakeistas',
    changeCompleteBody:
      'Visos sesijos atjungtos. Tęskite ir prisijunkite naudodami naują slaptažodį.',
    checkingPassword: 'Tikrinamas prisijungimo būdas…',
    error_invalid_input: 'Įveskite galiojantį el. pašto adresą.',
    error_password_mismatch: 'Slaptažodžiai nesutampa.',
    error_password_too_short: 'Slaptažodį turi sudaryti bent 15 simbolių.',
    error_password_compromised:
      'Šis slaptažodis žinomas iš ankstesnių duomenų saugumo pažeidimų. Pasirinkite unikalią slaptafrazę.',
    error_password_too_long: 'Slaptažodis per ilgas.',
    error_password_reused: 'Pasirinkite kitokį nei dabartinis slaptažodį.',
    error_current_password_wrong: 'Dabartinis slaptažodis neteisingas.',
    error_password_unavailable: 'Ši paskyra nenaudoja slaptažodžio.',
    error_password_changed: 'Slaptažodis pakeistas kitoje sesijoje. Bandykite dar kartą.',
    error_reset_expired: 'Nuoroda negalioja, jos galiojimo laikas baigėsi arba ji jau panaudota.',
    error_rate_limited: 'Per daug bandymų. Palaukite ir bandykite dar kartą.',
    error_server_unavailable: 'Šiuo metu paslauga nepasiekiama. Netrukus bandykite dar kartą.',
    error_generic: 'Kažkas nepavyko. Bandykite dar kartą.',
  },
  mt: {
    newPassword: 'Password ġdida (mill-inqas 15-il karattru)',
    confirmPassword: 'Ikkonferma l-password il-ġdida',
    showPassword: 'Uri l-password',
    hidePassword: 'Aħbi l-password',
    continueToLogin: 'Kompli għall-aċċess',
    changeTitle: 'Password',
    changeBody:
      'Daħħal il-password attwali tiegħek qabel tagħżel waħda ġdida. Meta tissejvja, toħroġ minn kull apparat.',
    currentPassword: 'Password attwali',
    changePassword: 'Ibdel il-password',
    passwordUnavailableTitle: "Tidħol permezz ta' Google",
    passwordUnavailableBody:
      "Dan il-kont m'għandux password ta' Poneva. Immaniġġja l-password ma' Google.",
    changeCompleteTitle: 'Il-password inbidlet',
    changeCompleteBody: 'Is-sessjonijiet kollha ngħalqu. Kompli u idħol bil-password il-ġdida.',
    checkingPassword: 'Qed niċċekkja l-metodu tal-aċċess…',
    error_invalid_input: 'Daħħal indirizz elettroniku validu.',
    error_password_mismatch: 'Il-passwords ma jaqblux.',
    error_password_too_short: 'Il-password għandu jkollha mill-inqas 15-il karattru.',
    error_password_compromised:
      "Din il-password tinsab f'dejta minn ksur preċedenti tas-sigurtà. Agħżel frażi sigrieta unika.",
    error_password_too_long: 'Il-password hija twila wisq.',
    error_password_reused: 'Agħżel password differenti minn dik attwali.',
    error_current_password_wrong: 'Il-password attwali mhijiex korretta.',
    error_password_unavailable: 'Dan il-kont ma jużax password.',
    error_password_changed: "Il-password inbidlet f'sessjoni oħra. Erġa' pprova.",
    error_reset_expired: 'Il-link mhuwiex validu, skada, jew diġà ntuża.',
    error_rate_limited: "Saru wisq tentattivi. Stenna ftit u erġa' pprova.",
    error_server_unavailable: "Is-servizz mhuwiex disponibbli bħalissa. Erġa' pprova dalwaqt.",
    error_generic: "Xi ħaġa marret ħażin. Erġa' pprova.",
  },
  pl: {
    newPassword: 'Nowe hasło (co najmniej 15 znaków)',
    confirmPassword: 'Potwierdź nowe hasło',
    showPassword: 'Pokaż hasło',
    hidePassword: 'Ukryj hasło',
    continueToLogin: 'Przejdź do logowania',
    changeTitle: 'Hasło',
    changeBody:
      'Wprowadź obecne hasło, zanim wybierzesz nowe. Zapisanie zmian wyloguje Cię ze wszystkich urządzeń.',
    currentPassword: 'Obecne hasło',
    changePassword: 'Zmień hasło',
    passwordUnavailableTitle: 'Logujesz się za pomocą Google',
    passwordUnavailableBody: 'To konto nie ma hasła Poneva. Zarządzaj hasłem w Google.',
    changeCompleteTitle: 'Hasło zmienione',
    changeCompleteBody:
      'Wszystkie sesje zostały wylogowane. Kontynuuj i zaloguj się za pomocą nowego hasła.',
    checkingPassword: 'Sprawdzanie metody logowania…',
    error_invalid_input: 'Wprowadź prawidłowy adres e-mail.',
    error_password_mismatch: 'Hasła nie są zgodne.',
    error_password_too_short: 'Hasło musi mieć co najmniej 15 znaków.',
    error_password_compromised:
      'To hasło jest znane z wcześniejszych wycieków danych. Wybierz unikalne hasło.',
    error_password_too_long: 'Hasło jest za długie.',
    error_password_reused: 'Wybierz hasło inne niż obecne.',
    error_current_password_wrong: 'Obecne hasło jest nieprawidłowe.',
    error_password_unavailable: 'To konto nie używa hasła.',
    error_password_changed: 'Hasło zostało zmienione w innej sesji. Spróbuj ponownie.',
    error_reset_expired: 'Link jest nieprawidłowy, wygasł lub został już użyty.',
    error_rate_limited: 'Zbyt wiele prób. Poczekaj chwilę i spróbuj ponownie.',
    error_server_unavailable: 'Usługa jest obecnie niedostępna. Spróbuj ponownie za chwilę.',
    error_generic: 'Coś poszło nie tak. Spróbuj ponownie.',
  },
  pt: {
    newPassword: 'Nova palavra-passe (pelo menos 15 caracteres)',
    confirmPassword: 'Confirmar nova palavra-passe',
    showPassword: 'Mostrar palavra-passe',
    hidePassword: 'Ocultar palavra-passe',
    continueToLogin: 'Continuar para iniciar sessão',
    changeTitle: 'Palavra-passe',
    changeBody:
      'Introduza a sua palavra-passe atual antes de escolher uma nova. Ao guardar, todas as sessões nos dispositivos serão terminadas.',
    currentPassword: 'Palavra-passe atual',
    changePassword: 'Alterar palavra-passe',
    passwordUnavailableTitle: 'Inicia sessão com o Google',
    passwordUnavailableBody:
      'Esta conta não tem uma palavra-passe da Poneva. Faça a gestão da palavra-passe através do Google.',
    changeCompleteTitle: 'Palavra-passe alterada',
    changeCompleteBody:
      'Todas as sessões foram terminadas. Continue e inicie sessão com a nova palavra-passe.',
    checkingPassword: 'A verificar o método de início de sessão…',
    error_invalid_input: 'Introduza um endereço de e-mail válido.',
    error_password_mismatch: 'As palavras-passe não coincidem.',
    error_password_too_short: 'A palavra-passe deve ter pelo menos 15 caracteres.',
    error_password_compromised:
      'Esta palavra-passe é conhecida por ter aparecido em fugas de dados anteriores. Escolha uma frase-passe única.',
    error_password_too_long: 'A palavra-passe é demasiado longa.',
    error_password_reused: 'Escolha uma palavra-passe diferente da atual.',
    error_current_password_wrong: 'A palavra-passe atual está incorreta.',
    error_password_unavailable: 'Esta conta não utiliza uma palavra-passe.',
    error_password_changed: 'A palavra-passe foi alterada noutra sessão. Tente novamente.',
    error_reset_expired: 'A ligação é inválida, expirou ou já foi utilizada.',
    error_rate_limited: 'Demasiadas tentativas. Aguarde um momento e tente novamente.',
    error_server_unavailable:
      'O serviço está indisponível neste momento. Tente novamente dentro de instantes.',
    error_generic: 'Ocorreu um erro. Tente novamente.',
  },
  ro: {
    newPassword: 'Parolă nouă (cel puțin 15 caractere)',
    confirmPassword: 'Confirmă parola nouă',
    showPassword: 'Afișează parola',
    hidePassword: 'Ascunde parola',
    continueToLogin: 'Continuă autentificarea',
    changeTitle: 'Parolă',
    changeBody:
      'Introdu parola actuală înainte de a alege una nouă. Salvarea te va deconecta de pe toate dispozitivele.',
    currentPassword: 'Parola actuală',
    changePassword: 'Schimbă parola',
    passwordUnavailableTitle: 'Te conectezi cu Google',
    passwordUnavailableBody: 'Acest cont nu are o parolă Poneva. Gestionează parola prin Google.',
    changeCompleteTitle: 'Parolă schimbată',
    changeCompleteBody:
      'Toate sesiunile au fost închise. Continuă și autentifică-te cu noua parolă.',
    checkingPassword: 'Se verifică metoda de autentificare…',
    error_invalid_input: 'Introdu o adresă de e-mail validă.',
    error_password_mismatch: 'Parolele nu coincid.',
    error_password_too_short: 'Parola trebuie să aibă cel puțin 15 caractere.',
    error_password_compromised:
      'Această parolă este cunoscută din scurgeri de date anterioare. Alege o frază de acces unică.',
    error_password_too_long: 'Parola este prea lungă.',
    error_password_reused: 'Alege o parolă diferită de cea actuală.',
    error_current_password_wrong: 'Parola actuală este incorectă.',
    error_password_unavailable: 'Acest cont nu folosește o parolă.',
    error_password_changed: 'Parola a fost schimbată într-o altă sesiune. Încearcă din nou.',
    error_reset_expired: 'Linkul este invalid, a expirat sau a fost deja folosit.',
    error_rate_limited: 'Prea multe încercări. Așteaptă puțin și încearcă din nou.',
    error_server_unavailable:
      'Serviciul nu este disponibil momentan. Încearcă din nou în scurt timp.',
    error_generic: 'Ceva nu a funcționat. Încearcă din nou.',
  },
  sk: {
    newPassword: 'Nové heslo (aspoň 15 znakov)',
    confirmPassword: 'Potvrďte nové heslo',
    showPassword: 'Zobraziť heslo',
    hidePassword: 'Skryť heslo',
    continueToLogin: 'Pokračovať na prihlásenie',
    changeTitle: 'Heslo',
    changeBody:
      'Pred výberom nového hesla zadajte svoje aktuálne heslo. Po uložení budete odhlásení zo všetkých zariadení.',
    currentPassword: 'Aktuálne heslo',
    changePassword: 'Zmeniť heslo',
    passwordUnavailableTitle: 'Prihlasujete sa cez Google',
    passwordUnavailableBody: 'Tento účet nemá heslo Poneva. Heslo spravujte cez Google.',
    changeCompleteTitle: 'Heslo zmenené',
    changeCompleteBody:
      'Zo všetkých relácií ste boli odhlásení. Pokračujte a prihláste sa novým heslom.',
    checkingPassword: 'Kontroluje sa spôsob prihlásenia…',
    error_invalid_input: 'Zadajte platnú e-mailovú adresu.',
    error_password_mismatch: 'Heslá sa nezhodujú.',
    error_password_too_short: 'Heslo musí obsahovať aspoň 15 znakov.',
    error_password_compromised:
      'Toto heslo je známe z predchádzajúcich únikov údajov. Vyberte jedinečnú heslovú frázu.',
    error_password_too_long: 'Heslo je príliš dlhé.',
    error_password_reused: 'Vyberte heslo, ktoré sa líši od aktuálneho hesla.',
    error_current_password_wrong: 'Aktuálne heslo nie je správne.',
    error_password_unavailable: 'Tento účet nepoužíva heslo.',
    error_password_changed: 'Heslo bolo zmenené v inej relácii. Skúste to znova.',
    error_reset_expired: 'Odkaz je neplatný, jeho platnosť vypršala alebo už bol použitý.',
    error_rate_limited: 'Príliš veľa pokusov. Chvíľu počkajte a skúste to znova.',
    error_server_unavailable: 'Služba je momentálne nedostupná. Skúste to znova o chvíľu.',
    error_generic: 'Niečo sa pokazilo. Skúste to znova.',
  },
  sl: {
    newPassword: 'Novo geslo (vsaj 15 znakov)',
    confirmPassword: 'Potrdite novo geslo',
    showPassword: 'Pokaži geslo',
    hidePassword: 'Skrij geslo',
    continueToLogin: 'Nadaljuj na prijavo',
    changeTitle: 'Geslo',
    changeBody:
      'Pred izbiro novega gesla vnesite trenutno geslo. Ob shranjevanju boste odjavljeni iz vseh naprav.',
    currentPassword: 'Trenutno geslo',
    changePassword: 'Spremeni geslo',
    passwordUnavailableTitle: 'Prijavljate se z Google Računom',
    passwordUnavailableBody: 'Ta račun nima gesla za Poneva. Geslo upravljajte v Googlu.',
    changeCompleteTitle: 'Geslo je spremenjeno',
    changeCompleteBody: 'Vse seje so odjavljene. Nadaljujte in se prijavite z novim geslom.',
    checkingPassword: 'Preverjanje načina prijave…',
    error_invalid_input: 'Vnesite veljaven e-poštni naslov.',
    error_password_mismatch: 'Gesli se ne ujemata.',
    error_password_too_short: 'Geslo mora vsebovati vsaj 15 znakov.',
    error_password_compromised:
      'To geslo je znano iz preteklih razkritij podatkov. Izberite edinstveno geselsko frazo.',
    error_password_too_long: 'Geslo je predolgo.',
    error_password_reused: 'Izberite geslo, ki se razlikuje od trenutnega.',
    error_current_password_wrong: 'Trenutno geslo ni pravilno.',
    error_password_unavailable: 'Ta račun ne uporablja gesla.',
    error_password_changed: 'Geslo je bilo spremenjeno v drugi seji. Poskusite znova.',
    error_reset_expired: 'Povezava ni veljavna, je potekla ali je bila že uporabljena.',
    error_rate_limited: 'Preveč poskusov. Počakajte trenutek in poskusite znova.',
    error_server_unavailable: 'Storitev trenutno ni na voljo. Poskusite znova čez nekaj trenutkov.',
    error_generic: 'Nekaj je šlo narobe. Poskusite znova.',
  },
  es: {
    newPassword: 'Nueva contraseña (al menos 15 caracteres)',
    confirmPassword: 'Confirmar nueva contraseña',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    continueToLogin: 'Continuar para iniciar sesión',
    changeTitle: 'Contraseña',
    changeBody:
      'Introduce tu contraseña actual antes de elegir una nueva. Al guardarla, se cerrará la sesión en todos los dispositivos.',
    currentPassword: 'Contraseña actual',
    changePassword: 'Cambiar contraseña',
    passwordUnavailableTitle: 'Inicias sesión con Google',
    passwordUnavailableBody:
      'Esta cuenta no tiene una contraseña de Poneva. Gestiona la contraseña con Google.',
    changeCompleteTitle: 'Contraseña cambiada',
    changeCompleteBody:
      'Se han cerrado todas las sesiones. Continúa e inicia sesión con la nueva contraseña.',
    checkingPassword: 'Comprobando el método de inicio de sesión…',
    error_invalid_input: 'Introduce una dirección de correo electrónico válida.',
    error_password_mismatch: 'Las contraseñas no coinciden.',
    error_password_too_short: 'La contraseña debe tener al menos 15 caracteres.',
    error_password_compromised:
      'Esa contraseña figura en filtraciones de datos anteriores. Elige una frase de contraseña única.',
    error_password_too_long: 'La contraseña es demasiado larga.',
    error_password_reused: 'Elige una contraseña diferente de la actual.',
    error_current_password_wrong: 'La contraseña actual no es correcta.',
    error_password_unavailable: 'Esta cuenta no utiliza una contraseña.',
    error_password_changed: 'La contraseña cambió en otra sesión. Inténtalo de nuevo.',
    error_reset_expired: 'El enlace no es válido, ha caducado o ya se ha utilizado.',
    error_rate_limited: 'Demasiados intentos. Espera un momento e inténtalo de nuevo.',
    error_server_unavailable:
      'El servicio no está disponible en este momento. Inténtalo de nuevo en unos instantes.',
    error_generic: 'Algo salió mal. Inténtalo de nuevo.',
  },
} as const;

export function passwordStringsFor(locale: string): PasswordStrings {
  const flat = catalog[locale.toLowerCase().split('-')[0] as keyof typeof catalog] ?? catalog.en;
  const result: Record<string, string> = {};
  const errors: Record<string, string> = {};
  for (const [key, value] of Object.entries(flat)) {
    if (key.startsWith('error_')) errors[key.slice(6)] = value;
    else result[key] = value;
  }
  return { ...result, errors } as PasswordStrings;
}
